import React, { useEffect } from 'react'
import { FormCheckboxField, FormSelectField } from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import {
  AddPlatformConfiguration,
  PlatformConfigurationTypesDto,
} from '../../../../api/Trading-Accounts/Types/PlatformConfigurationTypesDto'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { fetchTickmillCompaniesAction } from '../../../../store/identity/identity.actions'
import {
  fetchAllServerAction,
  fetchPlatformTypesAction,
} from '../../../../store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '../../../../utils/api/ApiClient'
import { IdentityApiClient } from '../../../../utils/api/identityApi'
import { TradingAccountsApiClient } from '../../../../utils/api/tradingAccountsApi'
import { isEmpty } from '../../../../utils/is-empty'

export interface AddEditPlatformConfigurationFormValues {
  tickmillCompanyId: number | null
  platformTypeId: number | null
  serverId: string | null
  demoServerId: string | null
  welcomeServerId?: string | null
  mamServerId?: string | null
  dynamicLeverage: boolean | null
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  data?: PlatformConfigurationTypesDto

  onSubmit(values: FormSubmitValues<AddPlatformConfiguration>): Promise<void>
}

const AddEditPlatformConfigurationFormUI: React.FC<
  FormikProps<AddEditPlatformConfigurationFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, isSubmitting, errors, touched, values, setFieldValue, isValid, data } =
    props

  const dispatch = useAppDispatch()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const platformServers = useAppSelector((state) => state.tradingAccounts.allServers)

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
    if (isEmpty(platformServers)) dispatch(fetchAllServerAction(tradingAccountsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = () => {
    handleSubmit()
  }

  const handlePlatformTypeChange = (selectedId: string | number | null | undefined) => {
    const nextPlatformTypeId = selectedId == null || selectedId === '' ? null : Number(selectedId)

    setFieldValue('platformTypeId', nextPlatformTypeId)
    setFieldValue('serverId', '')
    setFieldValue('demoServerId', '')
    setFieldValue('welcomeServerId', '')
    setFieldValue('mamServerId', '')
  }

  const filteredServers = platformServers.filter(
    (server) => server.platformType?.id === values.platformTypeId
  )
  const serverOptions = filteredServers.map((option) => ({
    id: option.id,
    name: option.publicName,
  }))

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {data ? 'Edit Platform Configuration' : 'Add Platform Configuration'}
          </DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormSelectField
              name='tickmillCompanyId'
              label='Tickmill Company'
              options={tickmillCompanies}
              error={touched.tickmillCompanyId ? errors.tickmillCompanyId : undefined}
              onChange={(value) =>
                props.setFieldValue('tickmillCompanyId', value ? Number(value) : null)
              }
            />
            <FormSelectField
              name='platformTypeId'
              label='Platform Type'
              options={platformTypes}
              error={touched.platformTypeId ? errors.platformTypeId : undefined}
              onChange={handlePlatformTypeChange}
            />
            <FormSelectField
              name='serverId'
              label='Live Server'
              options={serverOptions}
              error={touched.serverId ? errors.serverId : undefined}
              onChange={(value) => props.setFieldValue('serverId', value ? String(value) : null)}
            />
            <FormSelectField
              name='demoServerId'
              label='Demo Server'
              options={serverOptions}
              onChange={(value) =>
                props.setFieldValue('demoServerId', value ? String(value) : null)
              }
            />
            <FormSelectField
              name='welcomeServerId'
              label='Welcome Server'
              options={serverOptions}
              onChange={(value) =>
                props.setFieldValue('welcomeServerId', value ? String(value) : null)
              }
            />
            <FormSelectField
              name='mamServerId'
              label='MAM Server'
              options={serverOptions}
              onChange={(value) => props.setFieldValue('mamServerId', value ? String(value) : null)}
            />
            <FormCheckboxField
              name='dynamicLeverage'
              label='Dynamic Leverage'
              onChange={(value) => props.setFieldValue('dynamicLeverage', value)}
            />
          </div>
          <DialogFooter>
            <Button disabled={isSubmitting || !isValid} type='button' onClick={handleSubmitForm}>
              Save
            </Button>
          </DialogFooter>
        </Form>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

function dataToSubmit(
  values: AddEditPlatformConfigurationFormValues
): FormSubmitValues<AddPlatformConfiguration> {
  return {
    tickmillCompanyId: values.tickmillCompanyId || null,
    platformTypeId: values.platformTypeId || null,
    serverId: values.serverId || null,
    demoServerId: values.demoServerId || null,
    welcomeServerId: values.welcomeServerId || null,
    mamServerId: values.mamServerId || null,
    dynamicLeverage: values.dynamicLeverage || false,
  }
}

export const AddEditPlatformConfigurationForm = withFormik<
  OuterProps,
  AddEditPlatformConfigurationFormValues
>({
  mapPropsToValues: ({ data }) => {
    const currentPlatform: Partial<PlatformConfigurationTypesDto> = data || {}
    return {
      tickmillCompanyId: currentPlatform.tickmillCompany?.id ?? null,
      platformTypeId: currentPlatform.platformType?.id ?? null,
      serverId: currentPlatform.server?.id?.toString() ?? null,
      demoServerId: currentPlatform.demoServer?.id?.toString() ?? null,
      welcomeServerId: currentPlatform.welcomeServer?.id?.toString() ?? null,
      mamServerId: currentPlatform.mamServer?.id?.toString() ?? null,
      dynamicLeverage: currentPlatform.dynamicLeverage || false,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditPlatformConfigurationFormValues> = {}
    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'required'
    }
    if (!values.platformTypeId) {
      errors.platformTypeId = 'required'
    }
    if (!values.serverId) {
      errors.serverId = 'required'
    }

    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
})(AddEditPlatformConfigurationFormUI)
